import { useEffect, useRef, useState } from 'react';
import { CHOICE_TYPES, FIELD_LABEL, FORM_FIELD_TYPES, newFieldId, questionFormSchema, type FormField, type FormFieldType, type QuestionForm } from '@machs/shared';
import { useSync } from '../lib/sync';
import { Icon, type IconName } from '../lib/icons';
import { Dialog, Picker, useToast } from './ui';

// Rückfrage mit Fragebogen anlegen: beliebig viele Fragen mit passendem Eingabetyp

const TYPE_ICON: Record<FormFieldType, IconName> = {
  radio: 'check',
  checkbox: 'checks',
  select: 'down',
  text: 'text',
  textarea: 'text',
  number: 'plus',
  date: 'calendar',
  file: 'clip',
};

const emptyField = (type: FormFieldType = 'radio'): FormField => ({
  id: newFieldId(),
  type,
  label: '',
  help: '',
  required: false,
  options: CHOICE_TYPES.includes(type) ? ['', ''] : undefined,
  allowOther: false,
});

export function FormBuilderDialog({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const { client, me } = useSync();
  const toast = useToast();
  const [intro, setIntro] = useState('');
  // Ohne Antwortmöglichkeiten bleibt es eine Rückfrage, die frei im Chat beantwortet wird
  const [fields, setFields] = useState<FormField[]>([emptyField()]);
  const [allowVoice, setAllowVoice] = useState(true);
  const [questionTo, setQuestionTo] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // Hinweise stehen am Ende des Dialogs, deshalb dorthin scrollen
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  const update = (i: number, patch: Partial<FormField>) => setFields((list) => list.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, dir: -1 | 1) =>
    setFields((list) => {
      const next = [...list];
      const [item] = next.splice(i, 1);
      next.splice(i + dir, 0, item!);
      return next;
    });

  async function submit() {
    // Unberührte, leere Karten zählen nicht als Vorgabe
    const cleaned = fields
      .map((f) => ({
        ...f,
        label: f.label.trim(),
        help: f.help?.trim() || null,
        options: CHOICE_TYPES.includes(f.type) ? (f.options ?? []).map((o) => o.trim()).filter(Boolean) : undefined,
        allowOther: CHOICE_TYPES.includes(f.type) ? f.allowOther : undefined,
      }))
      .filter((f) => f.label || f.options?.length);
    if (!intro.trim()) return setError('Bitte die Frage eingeben');
    if (cleaned.length === 0) {
      await client.sendMessage({ taskId, kind: 'rueckfrage', body: intro.trim(), questionTo: questionTo || null });
      toast('Rückfrage gesendet');
      return onClose();
    }
    const parsed = questionFormSchema.safeParse({ fields: cleaned, allowVoice });
    if (!parsed.success) {
      const issue = parsed.error.issues[0]!;
      return setError(issue.path.includes('label') ? 'Bitte jede Antwortmöglichkeit benennen oder entfernen' : issue.message);
    }
    const form: QuestionForm = parsed.data;
    await client.sendMessage({ taskId, kind: 'rueckfrage', body: intro.trim(), form, questionTo: questionTo || null });
    toast('Rückfrage gesendet');
    onClose();
  }

  return (
    <Dialog
      title="Rückfrage"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => void submit()}><Icon name="send" size={17} />Rückfrage senden</button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="field">
          Deine Frage
          <textarea className="input min-h-[70px] py-3" value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="z. B. Wie soll die Startseite aufgebaut sein?" />
        </label>

        {fields.map((f, i) => (
          <div key={f.id} className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[12.5px] font-semibold text-[var(--muted)]">Frage {i + 1}</span>
              <span className="flex-1" />
              <button className="iconbtn plain" style={{ width: 30, height: 30 }} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Nach oben"><Icon name="back" size={15} style={{ transform: 'rotate(90deg)' }} /></button>
              <button className="iconbtn plain" style={{ width: 30, height: 30 }} disabled={i === fields.length - 1} onClick={() => move(i, 1)} aria-label="Nach unten"><Icon name="back" size={15} style={{ transform: 'rotate(-90deg)' }} /></button>
              <button className="iconbtn plain" style={{ width: 30, height: 30 }} onClick={() => setFields((l) => l.filter((_, j) => j !== i))} aria-label="Frage entfernen"><Icon name="trash" size={15} /></button>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-2 max-[560px]:grid-cols-1">
              <input className="input w-full" value={f.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Frage" maxLength={300} />
              <Picker
                label=""
                value={f.type}
                onChange={(type) => update(i, { type, options: CHOICE_TYPES.includes(type) ? (f.options?.length ? f.options : ['', '']) : undefined })}
                options={FORM_FIELD_TYPES.map((t) => ({ value: t, label: FIELD_LABEL[t] }))}
              />
            </div>
            <input className="input mt-2 h-10 w-full text-[14px]" value={f.help ?? ''} onChange={(e) => update(i, { help: e.target.value })} placeholder="Erklärung (optional): warum fragen wir das?" maxLength={2000} />

            {CHOICE_TYPES.includes(f.type) && (
              <div className="mt-2 flex flex-col gap-1.5">
                {(f.options ?? []).map((o, k) => (
                  <div key={k} className="flex items-center gap-2">
                    <Icon name={TYPE_ICON[f.type]} size={15} className="text-[var(--dim)]" />
                    <input
                      className="input h-9 flex-1 text-[14px]"
                      value={o}
                      onChange={(e) => update(i, { options: (f.options ?? []).map((x, m) => (m === k ? e.target.value : x)) })}
                      placeholder={`Antwort ${k + 1}`}
                      maxLength={200}
                    />
                    <button className="iconbtn plain" style={{ width: 28, height: 28 }} disabled={(f.options?.length ?? 0) <= 2} onClick={() => update(i, { options: (f.options ?? []).filter((_, m) => m !== k) })} aria-label="Antwort entfernen">
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                ))}
                <button className="self-start text-[13px] font-semibold text-[var(--brand-a)]" onClick={() => update(i, { options: [...(f.options ?? []), ''] })}>+ Antwort hinzufügen</button>
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              <button className="chip" aria-pressed={!!f.required} onClick={() => update(i, { required: !f.required })}>Pflichtfrage</button>
              {CHOICE_TYPES.includes(f.type) && (
                <button className="chip" aria-pressed={!!f.allowOther} onClick={() => update(i, { allowOther: !f.allowOther })}>Eigene Antwort erlauben</button>
              )}
            </div>
          </div>
        ))}

        <div className="field">
          <span>Antwortmöglichkeiten (optional)</span>
          <p className="m-0 text-[13px] text-[var(--muted)]">
            {fields.length === 0
              ? 'Ohne Vorgaben wird die Rückfrage frei im Chat beantwortet.'
              : 'Vorgaben machen die Antwort eindeutig; freie Eingaben sind genauso möglich.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(['radio', 'checkbox', 'text', 'date', 'file'] as FormFieldType[]).map((t) => (
            <button key={t} className="chip" onClick={() => setFields((l) => [...l, emptyField(t)])}>
              <Icon name={TYPE_ICON[t]} size={14} />+ {FIELD_LABEL[t]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 max-[560px]:grid-cols-1">
          <Picker
            label="Wer soll antworten?"
            value={questionTo}
            onChange={setQuestionTo}
            options={[{ value: '', label: 'Alle Beteiligten' }, ...me.members.filter((m) => m.userId !== me.user.id).map((m) => ({ value: m.userId, label: m.name }))]}
          />
          <div className="field">
            <span>Antworten</span>
            <button className="chip h-[46px] justify-center" aria-pressed={allowVoice} onClick={() => setAllowVoice(!allowVoice)}>
              <Icon name="mic" size={15} />Sprachantwort erlauben
            </button>
          </div>
        </div>

        {error && <div className="check" role="alert" ref={errorRef}><Icon name="alert" size={16} />{error}</div>}
      </div>
    </Dialog>
  );
}
